import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  approveReviewScript,
  deleteReviewPage,
  deleteReviewScript,
  flagReviewScript,
  getApproverStats,
  getFieldSuggestions,
  getReviewScript,
  getScannerStats,
  listDevices,
  listReviewScripts,
  patchPageRotation,
  patchReviewPage,
  patchReviewScript,
  reorderReviewPages,
  requestDeviceFlush,
  splitReviewScript,
  type PagePatchInput,
  type ReviewScriptDetail,
  type ScriptPatchInput,
} from "../lib/review-client";
import { settings } from "@/lib/settings";

export function useReviewScripts() {
  return useQuery({ queryKey: ["review", "scripts"], queryFn: listReviewScripts });
}

export function useReviewScript(id: string) {
  return useQuery({ queryKey: ["review", "scripts", id], queryFn: () => getReviewScript(id) });
}

export function usePatchScript(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: ScriptPatchInput) => patchReviewScript(id, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["review", "scripts", id] });
      queryClient.invalidateQueries({ queryKey: ["review", "scripts"] });
      // A just-saved label/subject/section becomes a suggestion for the next script.
      queryClient.invalidateQueries({ queryKey: ["review", "field-suggestions"] });
    },
  });
}

export function usePatchPage(scriptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pageId, patch }: { pageId: string; patch: PagePatchInput }) => patchReviewPage(pageId, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["review", "scripts", scriptId] });
    },
  });
}

export function useSetPageRotation(scriptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pageId, rotation }: { pageId: string; rotation: number }) => patchPageRotation(pageId, rotation),
    onMutate: async ({ pageId, rotation }) => {
      await queryClient.cancelQueries({ queryKey: ["review", "scripts", scriptId] });
      const previous = queryClient.getQueryData<ReviewScriptDetail>(["review", "scripts", scriptId]);
      if (previous) {
        queryClient.setQueryData(["review", "scripts", scriptId], {
          ...previous,
          pages: previous.pages.map(p => (p.id === pageId ? { ...p, rotation } : p)),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(["review", "scripts", scriptId], context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["review", "scripts", scriptId] });
    },
  });
}

export function useDeleteScript() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteReviewScript(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["review", "scripts"] });
    },
  });
}

export function useDeletePage(scriptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pageId: string) => deleteReviewPage(pageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["review", "scripts", scriptId] });
      queryClient.invalidateQueries({ queryKey: ["review", "scripts"] });
    },
  });
}

export function useSplitScript(scriptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pageIds: string[]) => splitReviewScript(scriptId, pageIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["review", "scripts", scriptId] });
      queryClient.invalidateQueries({ queryKey: ["review", "scripts"] });
    },
  });
}

export function useReorderPages(scriptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pageIds: string[]) => reorderReviewPages(scriptId, pageIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["review", "scripts", scriptId] });
    },
  });
}

export function useApproveScript(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => {
      const approvedBy = settings.getReviewerName()?.trim();
      if (!approvedBy) throw new Error("Reviewer name required");
      return approveReviewScript(id, approvedBy);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["review", "scripts", id] });
      queryClient.invalidateQueries({ queryKey: ["review", "scripts"] });
      queryClient.invalidateQueries({ queryKey: ["review", "approver-stats"] });
    },
  });
}

export function useFlagScript(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (message: string) => flagReviewScript(id, message),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["review", "scripts", id] });
      queryClient.invalidateQueries({ queryKey: ["review", "scripts"] });
      queryClient.invalidateQueries({ queryKey: ["review", "approver-stats"] });
    },
  });
}

export function useFieldSuggestions() {
  return useQuery({ queryKey: ["review", "field-suggestions"], queryFn: getFieldSuggestions });
}

/** Refetches on its own: this is a live view of which phones are stuck, so
 * a desk with it open should see a backlog drain without being told to
 * reload. Devices beat every 20s, so polling faster buys nothing. */
export function useDevices() {
  return useQuery({
    queryKey: ["review", "devices"],
    queryFn: listDevices,
    refetchInterval: 20000,
  });
}

export function useRequestDeviceFlush() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deviceId: string) => requestDeviceFlush(deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["review", "devices"] });
    },
  });
}

export function useScannerStats(from: string, to: string) {
  return useQuery({ queryKey: ["review", "stats", from, to], queryFn: () => getScannerStats(from, to) });
}

export function useApproverStats(from: string, to: string) {
  return useQuery({
    queryKey: ["review", "approver-stats", from, to],
    queryFn: () => getApproverStats(from, to),
  });
}
